/**
 * Auto-instrumentation — single delegated click handler at `document` level.
 *
 * Fires GA4-canonical events without needing to wire every individual
 * `<a>` / `<button>`:
 *
 *   - outbound_click     — anchor whose host !== current host
 *   - internal_link_click — anchor inside an article body (`<article>` or `[data-track-internal]`)
 *   - CTA_click          — element marked with `data-cta` or `data-cta-name`
 *
 * Mount with `installAutoTrack()` from App.js. Idempotent — installing twice
 * is a no-op.
 *
 * Why delegation: zero per-component instrumentation, survives React
 * re-renders, works for content rendered from markdown / 3rd-party HTML,
 * and stays consent-aware via the unified facade.
 */
import { trackEvent } from "./tracking";

let installed = false;

function getHrefAndText(el) {
  const href = el.getAttribute("href") || "";
  const text = (el.innerText || el.textContent || "").trim().slice(0, 80);
  return { href, text };
}

function isExternal(href) {
  if (!href) return false;
  if (href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return false;
  if (href.startsWith("/") || href.startsWith("?")) return false;
  try {
    const u = new URL(href, window.location.href);
    return u.host && u.host !== window.location.host;
  } catch {
    return false;
  }
}

function classifyInternalContext(el) {
  // Internal-link events fire only inside content surfaces (articles, learn,
  // landing CTAs marked with [data-track-internal]). This keeps GA4 reports
  // focused on editorial/CTA-driven internal traffic rather than every navbar tap.
  const inArticle = !!el.closest("article, [data-track-internal]");
  return inArticle;
}

function handleClick(e) {
  // Use composedPath to support shadow DOM + ensure we find the nearest <a> / cta target.
  const path = typeof e.composedPath === "function" ? e.composedPath() : [];
  let anchor = null;
  let ctaTarget = null;
  for (const node of path) {
    if (!node || node.nodeType !== 1) continue;
    if (!anchor && node.tagName === "A") anchor = node;
    if (!ctaTarget && (node.dataset?.cta || node.dataset?.ctaName)) ctaTarget = node;
    if (anchor && ctaTarget) break;
  }

  if (ctaTarget) {
    const name = ctaTarget.dataset.ctaName || ctaTarget.dataset.cta || "unnamed_cta";
    const { text } = getHrefAndText(ctaTarget);
    trackEvent("CTA_click", {
      cta_name: name,
      cta_text: text,
      cta_location: ctaTarget.dataset.ctaLocation || window.location.pathname,
    });
  }

  if (anchor) {
    const { href, text } = getHrefAndText(anchor);
    if (!href) return;
    if (isExternal(href)) {
      trackEvent("outbound_click", {
        href,
        link_text: text,
        link_location: window.location.pathname,
      });
    } else if (classifyInternalContext(anchor)) {
      trackEvent("internal_link_click", {
        href,
        link_text: text,
        link_location: window.location.pathname,
      });
    }
  }
}

let lastScrollDepth = 0;
let articleScrollHandlersBound = false;

/**
 * Article scroll milestones — fires `article_read_50` and `article_read_90`
 * exactly once per article view. Re-evaluated when the route changes
 * (handled inside RouteAnalytics via `resetScrollMilestones`).
 */
function getArticleScrollDepth() {
  // Prefer measuring against the closest <article> if present.
  const article = document.querySelector("article");
  if (article) {
    const rect = article.getBoundingClientRect();
    const total = Math.max(1, article.offsetHeight - window.innerHeight);
    const scrolled = Math.min(total, Math.max(0, -rect.top));
    return Math.round((scrolled / total) * 100);
  }
  const doc = document.documentElement;
  const total = Math.max(1, doc.scrollHeight - window.innerHeight);
  const scrolled = Math.min(total, window.scrollY || 0);
  return Math.round((scrolled / total) * 100);
}

const SCROLL_FIRED = new Set(); // tracks which milestones fired for the current page

function handleScroll() {
  // Only meaningful when an <article> exists OR when path starts with /learn.
  if (!document.querySelector("article") && !window.location.pathname.startsWith("/learn/")) return;
  const depth = getArticleScrollDepth();
  if (depth <= lastScrollDepth) return;
  lastScrollDepth = depth;
  if (depth >= 50 && !SCROLL_FIRED.has(50)) {
    SCROLL_FIRED.add(50);
    trackEvent("article_read_50", {
      path: window.location.pathname,
      article_title: document.title,
    });
  }
  if (depth >= 90 && !SCROLL_FIRED.has(90)) {
    SCROLL_FIRED.add(90);
    trackEvent("article_read_90", {
      path: window.location.pathname,
      article_title: document.title,
    });
  }
}

/** Called by RouteAnalytics on every route change. */
export function resetScrollMilestones() {
  SCROLL_FIRED.clear();
  lastScrollDepth = 0;
}

export function installAutoTrack() {
  if (installed || typeof document === "undefined") return;
  document.addEventListener("click", handleClick, { capture: true, passive: true });
  if (!articleScrollHandlersBound) {
    // Passive scroll listener — minimal main-thread cost.
    window.addEventListener("scroll", handleScroll, { passive: true });
    articleScrollHandlersBound = true;
  }
  installed = true;
}
